import type { PlacedSymbol } from '@/config/sitac';
import type { WindDataSource } from '../../map/layers/WindOverlay';

export const getSitacStorageKey = (eventId?: string) =>
  eventId ? `DOS518_SITAC_STATE_${eventId}` : 'DOS518_SITAC_STATE';

export const normalizeDegrees = (deg: number) => ((deg % 360) + 360) % 360;

export const metersToLatLng = (
  origin: [number, number],
  distanceM: number,
  bearingDeg: number
): [number, number] => {
  const angle = (bearingDeg * Math.PI) / 180;
  const dLat = (distanceM * Math.cos(angle)) / 111320;
  const dLng =
    (distanceM * Math.sin(angle)) /
    (111320 * Math.cos((origin[0] * Math.PI) / 180));
  return [origin[0] + dLat, origin[1] + dLng];
};

export const computePolygonCentroid = (
  points: [number, number][]
): [number, number] | null => {
  if (points.length === 0) return null;

  const sum = points.reduce(
    (acc, [lat, lng]) => {
      acc[0] += lat;
      acc[1] += lng;
      return acc;
    },
    [0, 0] as [number, number]
  );

  return [sum[0] / points.length, sum[1] / points.length];
};

export const getVectorFromBearing = (
  bearingDeg: number
): [number, number] => {
  const angle = (bearingDeg * Math.PI) / 180;
  return [Math.cos(angle), Math.sin(angle)];
};

export const distanceMeters = (
  a: [number, number],
  b: [number, number]
): number => {
  const R = 6371000;
  const lat1 = (a[0] * Math.PI) / 180;
  const lat2 = (b[0] * Math.PI) / 180;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  return 2 * R * Math.asin(Math.sqrt(h));
};

export const getPerpendicularRightVector = (
  bearingDeg: number
): [number, number] => {
  const [vx, vy] = getVectorFromBearing(bearingDeg);
  return [vy, -vx];
};

export const computeDirectionalSegment = (
  points: [number, number][],
  perimetroChiuso: boolean,
  vector: [number, number],
  mode: 'max' | 'min' = 'max',
  excludeKeys: Set<string> = new Set()
): [[number, number], [number, number]] | null => {
  if (points.length < 2) return null;

  const centroid = computePolygonCentroid(points) ?? points[0];
  const [vx, vy] = vector;
  const segmentCount = perimetroChiuso ? points.length : points.length - 1;

  let bestScore =
    mode === 'max' ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  let bestSegment: [[number, number], [number, number]] | null = null;

  for (let i = 0; i < segmentCount; i += 1) {
    const start = points[i];
    const end = points[(i + 1) % points.length];
    if (!start || !end) continue;

    const segmentKey = `${start[0]},${start[1]}|${end[0]},${end[1]}`;
    const reverseKey = `${end[0]},${end[1]}|${start[0]},${start[1]}`;

    if (excludeKeys.has(segmentKey) || excludeKeys.has(reverseKey)) {
      continue;
    }

    const midLat = (start[0] + end[0]) / 2;
    const midLng = (start[1] + end[1]) / 2;
    const dx = midLat - centroid[0];
    const dy = midLng - centroid[1];
    const score = dx * vx + dy * vy;

    if (
      (mode === 'max' && score > bestScore) ||
      (mode === 'min' && score < bestScore)
    ) {
      bestScore = score;
      bestSegment = [start, end];
    }
  }

  return bestSegment;
};

export const getSegmentKey = (
  segment?: [[number, number], [number, number]] | null
): string | null => {
  if (!segment) return null;
  const [start, end] = segment;
  return `${start[0]},${start[1]}|${end[0]},${end[1]}`;
};

export const computeActiveFrontSegment = (
  points: [number, number][],
  perimetroChiuso: boolean,
  propagationDeg: number
): [[number, number], [number, number]] | null =>
  computeDirectionalSegment(
    points,
    perimetroChiuso,
    getVectorFromBearing(propagationDeg),
    'max'
  );

export const computeShelteredRearSegment = (
  points: [number, number][],
  perimetroChiuso: boolean,
  propagationDeg: number
): [[number, number], [number, number]] | null =>
  computeDirectionalSegment(
    points,
    perimetroChiuso,
    getVectorFromBearing(propagationDeg),
    'min'
  );

export const computeRightFlankSegment = (
  points: [number, number][],
  perimetroChiuso: boolean,
  propagationDeg: number,
  excludeKeys: Set<string> = new Set()
): [[number, number], [number, number]] | null =>
  computeDirectionalSegment(
    points,
    perimetroChiuso,
    getPerpendicularRightVector(propagationDeg),
    'max',
    excludeKeys
  );

export const computeLeftFlankSegment = (
  points: [number, number][],
  perimetroChiuso: boolean,
  propagationDeg: number,
  excludeKeys: Set<string> = new Set()
): [[number, number], [number, number]] | null =>
  computeDirectionalSegment(
    points,
    perimetroChiuso,
    getPerpendicularRightVector(propagationDeg),
    'min',
    excludeKeys
  );

export const getMinutesSince = (iso?: string | null): number | null => {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return Math.max(0, (Date.now() - parsed.getTime()) / 60000);
};

export interface TacticalMapSavedState {
  placedSymbols: PlacedSymbol[];
  puntiPerimetro: [number, number][];
  perimetroChiuso: boolean;
  mapCenter?: [number, number];
  mapZoom?: number;
  windDirectionDeg?: number;
  windSpeedKmh?: number;
  windDataSource?: WindDataSource;
  windUpdatedAt?: string | null;
  windSourceLabel?: string;
  windRefreshIntervalMin?: number;
  windStaleAfterMin?: number;
  puntoInnesco?: [number, number] | null;
  tipoVegetazione?: string;
  noteOperative?: string;
}

// ─────────────────────────────────────────────────────────────
// Estensione TacticalMapSavedState con puntoInnesco
// ─────────────────────────────────────────────────────────────
